import Grid from "@mui/material/Grid";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import AssignmentSummaryCard from "../features/assignment/components/AssignmentSummaryCard";
import QuizGroupSummaryCard from "../features/quiz/components/QuizGroupSummaryCard";

export const UpcomingAssignmentsAndQuizzes = ({
  assignments,
  quizzes,
  studentView = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");

  const statusOptions = ["Upcoming", "Past Deadline"];
  const availableTypes = ["Assignment", "Quiz"];

  const getDerivedStatus = (a) => {
    if (a.status) return a.status;
    if (!a.dueDate) return "";
    try {
      const due = new Date(a.dueDate);
      const now = new Date();
      return due.getTime() >= now.getTime() ? "Upcoming" : "Past Deadline";
    } catch {
      return "";
    }
  };

  const filteredResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let combined = [];
    if (selectedType == "Assignment") {
      combined = assignments;
    } else if (selectedType == "Quiz") {
      combined = quizzes;
    } else {
      combined = [...assignments, ...quizzes];
    }
    return combined
      .filter((v) =>
        selectedClass ? v.schoolClass.name === selectedClass : true,
      )
      .filter((a) =>
        selectedStatus ? getDerivedStatus(a) === selectedStatus : true,
      )
      .filter((v) => {
        if (!term) return true;
        const tagStrings = Array.isArray(v?.tags)
          ? v?.tags.map((t) => (typeof t === "string" ? t : t.tag || ""))
          : [];
        const haystacks = [v.name, v.schoolClass?.name || "", ...tagStrings];
        return haystacks.some((h) => h.toLowerCase().includes(term));
      });
  }, [
    assignments,
    quizzes,
    searchTerm,
    selectedClass,
    selectedStatus,
    selectedType,
  ]);

  const availableClasses = useMemo(() => {
    const classesSet = new Set(filteredResults.map((a) => a.schoolClass.name));
    return Array.from(classesSet);
  }, [filteredResults]);

  return (
    <Grid
      container
      spacing={2}
      sx={{ width: "100%", mt: 6 }}
      data-testid={"fruitify-assignment-search"}
    >
      <Grid size={12} sx={{ mb: 1 }}>
        <Typography
          variant="overline"
          color="secondary.main"
          sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
        >
          Your work
        </Typography>
        <Typography variant="h3">Upcoming Assignments and Quizzes</Typography>
      </Grid>
      <Grid
        size={12}
        sx={{
          mb: 2,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: "background.paper",
          boxShadow: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: "100%" }}
        >
          <TextField
            label="Search Keyword"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: "100%", flex: 1, minWidth: { sm: 0 } }}
          />

          <FormControl sx={{ width: "100%", flex: 1, minWidth: { sm: 0 } }}>
            <InputLabel id="school-class-label">Class</InputLabel>
            <Select
              labelId="school-class-label"
              value={selectedClass}
              label="Class"
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {availableClasses.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: "100%", flex: 1, minWidth: { sm: 0 } }}>
            <InputLabel id="quiz-or-assignment-label">Type</InputLabel>
            <Select
              labelId="quiz-or-assignment-label"
              value={selectedType}
              label="Type"
              onChange={(e) => setSelectedType(e.target.value)}
            >
              {" "}
              <MenuItem value="">All</MenuItem>
              {availableTypes.map((val) => (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: "100%", flex: 1, minWidth: { sm: 0 } }}>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Grid>
      {filteredResults.map((obj, index) => (
        <Grid size={{ xs: 12, md: 4 }} key={index} sx={{ display: "flex" }}>
          {obj.__typename === "AssignmentNode" ? (
            <AssignmentSummaryCard
              assignment={obj}
              studentView={studentView}
              showTypeChip
            />
          ) : (
            <QuizGroupSummaryCard
              quizGroup={obj}
              studentView={studentView}
              showTypeChip
            />
          )}
        </Grid>
      ))}
    </Grid>
  );
};
